import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MapPin, Clock } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ContattiPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ nome: "", email: "", messaggio: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, nome: `${user.nome} ${user.cognome}`, email: user.email }));
    }
  }, [user]);

  useEffect(() => {
    const msg = params.get("messaggio");
    if (msg) setForm((f) => ({ ...f, messaggio: msg }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contatti", form);
      setSent(true);
      toast.success("Messaggio inviato a ENPALSBOX");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6" data-testid="contatti-page">
      <h1 className="section-title">Contattaci</h1>
      <p className="mt-3 text-muted-foreground">
        Hai una domanda o hai bisogno di assistenza? Scrivici: il team ENPALSBOX ti risponderà al più presto.
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Seguici anche su{" "}
        <a href="https://www.instagram.com/enpalsbox" target="_blank" rel="noopener noreferrer" data-testid="contatti-instagram-link" className="font-semibold text-bordeaux hover:underline">
          Instagram
        </a>
        .
      </p>

      <div className="card-elegant mt-6 space-y-3" data-testid="contatti-recapiti">
        <h2 className="font-heading text-lg font-semibold text-bordeaux-dark">I nostri recapiti</h2>
        <p className="flex items-center gap-2 text-sm text-foreground/80" data-testid="contatti-tel-1">
          <Phone className="h-4 w-4 shrink-0 text-bordeaux" />
          <a href="tel:+393791880225" className="font-semibold text-bordeaux hover:underline">379 188 0225</a>
        </p>
        <p className="flex items-center gap-2 text-sm text-foreground/80" data-testid="contatti-tel-2">
          <Phone className="h-4 w-4 shrink-0 text-bordeaux" />
          <a href="tel:+393318296690" className="font-semibold text-bordeaux hover:underline">331 829 6690</a>
        </p>
        <p className="flex items-center gap-2 text-sm text-foreground/80" data-testid="contatti-tel-ufficio">
          <Clock className="h-4 w-4 shrink-0 text-bordeaux" />
          <span>Ufficio: <a href="tel:+39050878852" className="font-semibold text-bordeaux hover:underline">050 878852</a> (lun–ven)</span>
        </p>
        <p className="flex items-center gap-2 text-sm text-foreground/80" data-testid="contatti-indirizzo">
          <MapPin className="h-4 w-4 shrink-0 text-bordeaux" />
          <span>Via G. Carducci 64/C – Ghezzano (PI)</span>
        </p>
      </div>

      {sent ? (
        <div className="card-elegant mt-8 border-bordeaux/30 bg-bordeaux-ultralight" data-testid="contatti-success">
          <h2 className="font-heading text-xl font-semibold text-bordeaux-dark">Messaggio inviato</h2>
          <p className="mt-2 text-sm text-foreground/80">
            Grazie per averci contattato. Il tuo messaggio è stato registrato e inviato a ENPALSBOX: ti risponderemo al più presto all'indirizzo email indicato.
          </p>
          <button onClick={() => { setSent(false); setForm((f) => ({ ...f, messaggio: "" })); }} data-testid="contatti-new-message-btn" className="btn-outline-bordeaux mt-4">
            INVIA UN ALTRO MESSAGGIO
          </button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="card-elegant mt-8 space-y-5" data-testid="contatti-form">
          <div>
            <label className="label-elegant" htmlFor="contatti-nome">Nome *</label>
            <input id="contatti-nome" data-testid="contatti-nome-input" className="input-elegant" required value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>
          <div>
            <label className="label-elegant" htmlFor="contatti-email">Email *</label>
            <input id="contatti-email" type="email" data-testid="contatti-email-input" className="input-elegant" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label-elegant" htmlFor="contatti-messaggio">Messaggio *</label>
            <textarea id="contatti-messaggio" rows={5} data-testid="contatti-messaggio-input" className="input-elegant" required value={form.messaggio}
              onChange={(e) => setForm({ ...form, messaggio: e.target.value })} />
          </div>
          <button type="submit" disabled={loading} data-testid="contatti-submit-button" className="btn-bordeaux w-full">
            {loading ? "INVIO IN CORSO..." : "INVIA MESSAGGIO"}
          </button>
        </form>
      )}
    </div>
  );
}
